import { type FC, Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Loader } from "../common/Loader";
import { useCompany } from "../company/useCompany";
import { Select } from "../components/Select";
import { useSwitchMetaAdsClient } from "./MetaAdsSwitchContext";
import { useMetaAdsOverview } from "./useMetaAdsOverview";

const MetaAdsClientSwitcherInner: FC = () => {
	const company = useCompany();
	const { data: overview } = useMetaAdsOverview();
	const { switchClient, isSwitching, switchingLabel } =
		useSwitchMetaAdsClient();

	const connectedClients = overview.filter((item) => item.connected);
	const options =
		connectedClients.length > 0
			? connectedClients
			: overview.filter((item) => item.companyId === company.id);

	if (options.length < 2) {
		return (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 8,
					fontSize: 14,
				}}
			>
				<span className="text-muted">Client:</span>
				<strong>{company.name}</strong>
				{isSwitching && (
					<span
						className="text-muted"
						style={{ display: "inline-flex", gap: 6 }}
					>
						<Loader size={14} /> Switching…
					</span>
				)}
			</div>
		);
	}

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				gap: 8,
				fontSize: 14,
			}}
		>
			<label htmlFor="meta-ads-client-switcher" className="text-muted">
				Client:
			</label>
			<Select
				id="meta-ads-client-switcher"
				size="sm"
				style={{ width: "auto", minWidth: 200 }}
				value={company.id}
				disabled={isSwitching}
				onChange={(e) => {
					const nextId = Number(e.target.value);
					const option = options.find((item) => item.companyId === nextId);
					switchClient(nextId, {
						name: option?.companyName,
					});
				}}
			>
				{options.map((item) => (
					<option key={item.companyId} value={item.companyId}>
						{item.companyName}
						{item.adAccountName ? ` — ${item.adAccountName}` : ""}
					</option>
				))}
			</Select>
			{isSwitching && (
				<span
					className="text-muted"
					style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
					aria-live="polite"
				>
					<Loader size={14} />
					Loading {switchingLabel ?? "client"}…
				</span>
			)}
		</div>
	);
};

export const MetaAdsClientSwitcher: FC = () => (
	<ErrorBoundary fallback={null}>
		<Suspense fallback={null}>
			<MetaAdsClientSwitcherInner />
		</Suspense>
	</ErrorBoundary>
);
